import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined"
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined"
import TableRestaurantOutlined from "@mui/icons-material/TableRestaurantOutlined"
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Divider from "@mui/material/Divider"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { styled } from "@mui/material/styles"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import { formatCurrency } from "../../../common/utils"
import IconTitleValue from "../candidate-details/IconTitleValue"
import DialogHeader from "../components/DialogHeader"
import OfferStatusTextField from "../components/OfferStatusTextField"
import PerksColumns from "../components/offers/PerksColumns"
import { OFFER_ORDERED_STATUSES, useDialogContainer } from "../utils"
import { commissionRatioDisplay } from "../utils"

export default function OfferDetailsDialog({
  open,
  onClose,
  offer: listOffer,
}) {
  const [offer, setOffer] = useState({
    template: {
      signing_bonus: 0,
      commission_tiers: [],
      per_transaction_fee: 0,
      monthly_desk_fee: 0,
      perks: [],
      custom_perks: [],
    },
    status: OFFER_ORDERED_STATUSES[0],
  })

  const [isLoading, setIsLoading] = useState(false)
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()
  const offerAPIURL = `/crm/api/offers/${listOffer?.id}/`

  function handleSoftClose() {
    onClose()
  }

  function handleSaveButtonClicked() {
    setIsLoading(true)
    axios
      .put(offerAPIURL, { status: offer.status })
      .then(({ data: offer }) => {
        onClose(offer)
      })
      .finally(() => setIsLoading(false))
  }

  function handleViewCandidateButtonClicked() {
    onClose()
    navigate(`/candidates/${offer.candidate.id}`)
  }

  useEffect(
    () => {
      if (!listOffer) {
        return
      }
      setIsLoading(true)
      axios
        .get(offerAPIURL)
        .then(({ data }) => setOffer(data))
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listOffer]
  )

  return (
    <Container
      open={open}
      onClose={onClose}
      anchor="right"
      sx={{ zIndex: 1201 }}
    >
      <DialogHeader title={offer.template.name} onClose={onClose} />
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <DialogContent>
        {isLoading ? (
          <>
            <Column gap={1}>
              <Skeleton variant="text" width={200} height={40} />
              <Skeleton variant="text" width="100%" height={40} />
              <Skeleton
                variant="rectangular"
                width={200}
                height={20}
                sx={{ mt: 2 }}
              />
            </Column>
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonSection key={i} addSeparator={i < 2} />
            ))}
          </>
        ) : (
          <>
            <Column gap={1}>
              <Typography variant="h5">Introduction</Typography>
              <Typography variant="body1" color="text.secondary">
                {offer.template.introduction}
              </Typography>
              <Column sx={{ mt: 2 }}>
                <OfferStatusTextField
                  status={offer.status}
                  setStatus={(status) => setOffer({ ...offer, status })}
                />
              </Column>
            </Column>
            <SectionColumn>
              <Typography variant="h6">Compensation</Typography>
              <Grid container>
                <Grid xs={4}>
                  <IconTitleValue
                    Icon={TrendingUpOutlinedIcon}
                    title="Signing Bonus"
                    value={formatCurrency(offer.template.signing_bonus)}
                  />
                </Grid>
                <Grid xs={4} xsOffset={1}>
                  <IconTitleValue
                    Icon={BalanceOutlinedIcon}
                    title="Commission Ratio"
                    value={
                      offer.template.commission_tiers.length > 0
                        ? commissionRatioDisplay(
                            offer.template.commission_tiers
                          )
                        : "--"
                    }
                  />
                </Grid>
              </Grid>
            </SectionColumn>
            <StyledDivider />
            <SectionColumn>
              <Typography variant="h6">Fees</Typography>
              <Grid container>
                <Grid xs={4}>
                  <IconTitleValue
                    Icon={HandshakeOutlinedIcon}
                    title="Per transaction fee"
                    value={formatCurrency(offer.template.per_transaction_fee)}
                  />
                </Grid>
                <Grid xs={4} xsOffset={1}>
                  <IconTitleValue
                    Icon={TableRestaurantOutlined}
                    title="Monthly Desk Fee"
                    value={formatCurrency(offer.template.monthly_desk_fee)}
                  />
                </Grid>
              </Grid>
            </SectionColumn>
            <StyledDivider />
            <SectionColumn>
              <Typography variant="h6">Perks</Typography>
              <PerksColumns
                perks={offer.template.perks.concat(offer.template.custom_perks)}
              />
            </SectionColumn>
            <StyledDivider />
            <SectionColumn>
              <Typography variant="h6">Notes</Typography>
              <Typography variant="body1" color="text.secondary">
                {offer.template.note || "--"}
              </Typography>
            </SectionColumn>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="secondary"
          disabled={isLoading}
          onClick={handleViewCandidateButtonClicked}
        >
          View candidate
        </Button>
        <Button variant="outlined" color="primary" onClick={handleSoftClose}>
          Close
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isLoading}
          onClick={handleSaveButtonClicked}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}

function SkeletonSection({ addSeparator }) {
  return (
    <>
      <SectionColumn>
        <Skeleton variant="text" width={150} height={30} />
        <Grid container>
          <SideSkeleton />
          <SideSkeleton xsOffset={1} />
        </Grid>
      </SectionColumn>
      {addSeparator && <StyledDivider />}
    </>
  )
}

function SideSkeleton({ xsOffset }) {
  return (
    <Grid xs={4} xsOffset={xsOffset}>
      <Grid container alignItems="center" spacing={1}>
        <Grid xs={4}>
          <Skeleton variant="rounded" width={40} height={40} />
        </Grid>
        <Grid xs={4} xsOffset={1}>
          <Skeleton variant="text" width={140} height={20} />
          <Skeleton variant="text" width={40} height={20} />
        </Grid>
      </Grid>
    </Grid>
  )
}

const SectionColumn = styled(Column)(({ theme }) => ({
  marginTop: theme.spacing(3),
  gap: theme.spacing(2),
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(3),
}))
